import { Vote, Crosshair, Heart } from "lucide-react";
import Layout from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { OrgShape } from "@/components/Decorations";

const Elections = () => (
  <Layout>
    <PageHero badge="Politiikka">Politiikka</PageHero>

    {/* ── AIKAISEMMAT VAALIT & TAVOITTEET ── */}
    <section
      className="relative overflow-hidden px-6 py-16 lg:px-10"
      style={{
        background: "linear-gradient(180deg, hsl(39 88% 79%) 0%, hsl(40 86% 73%) 100%)",
      }}
    >
      <OrgShape
        className="-right-20 bottom-[-3rem] h-[280px] w-[320px]"
        color="radial-gradient(circle at 45% 45%, hsla(42,96%,84%,0.7) 0%, hsla(41,90%,64%,0.35) 62%, hsla(41,88%,56%,0) 100%)"
      />
      <svg className="pointer-events-none absolute left-[4%] top-[15%] hidden h-16 w-16 rotate-[-10deg] lg:block" viewBox="0 0 100 100" fill="none" aria-hidden="true">
        <path d="M50,90 C50,90 10,60 10,35 C10,20 22,8 36,8 C44,8 50,14 50,14 C50,14 56,8 64,8 C78,8 90,20 90,35 C90,60 50,90 50,90Z" fill="hsla(0,0%,100%,0.4)" />
      </svg>

      <div className="relative mx-auto max-w-5xl">
        <div className="relative lg:pl-8">

          {/* ── POLITIIKKA ON TEKOJA ── */}
          <div className="mb-16">
            <div className="relative mb-10 flex items-center gap-3">
              
              <h2 className="font-display text-2xl font-bold italic leading-snug text-foreground md:text-3xl">
                Politiikka on tekoja. Se on vastuullista taloudenpitoa, sivistyksen vaalimista ja tulevaisuuden rakentamista pitkäjänteisesti.
              </h2>
            </div>

            <div className="rounded-2xl border border-border bg-card/85 p-8 shadow-sm md:p-12">
              <p className="text-lg leading-relaxed text-foreground/80">
                Olen mukana politiikassa, koska haluan olla osana muutosvoimaa kohti kestävämpää ja kohtaavampaa tulevaisuutta. Politiikka vaatii tekijältään <strong className="font-bold text-secondary">päättäväisyyttä</strong>, <strong className="font-bold text-secondary">päätöksentekokykyä</strong> ja <strong className="font-bold text-secondary">pitkäjänteisyyttä</strong>. Tulevaisuutta ei rakenneta yhdessä yössä. Päättäväisyys näkyy tavassani työskennellä: asioita tehdään nousujohteisesti sinisellä sydämellä. Päätöksenteossa mielestäni tärkeintä on toimia yhdessä ja nähdä päätösten vaikutukset erilaisten ihmisten arjessa. Päätöksiä ei myöskään tehdä juosten, on poliitikon vastuu perehtyä päätettäviin asioihin ja olla läsnä. Päätöksissä talous, mahdollisuuksien tasa-arvo ja vaikuttavuus ovat keskeisessä roolissa.
              </p>
            </div>
          </div>

          {/* ── EDUSKUNTAVAALIT 2027 ── */}
          <div className="mb-16">
            <div className="relative mb-10 flex items-center gap-3">
              
              <h2 className="font-display text-3xl font-bold italic text-foreground md:text-4xl">
                Tulossa eduskuntavaalit 2027
              </h2>
            </div>

            <div className="rounded-2xl border border-border bg-card/85 p-8 shadow-sm md:p-12">
              <p className="text-lg leading-relaxed text-foreground/80">
                Olen ehdolla eduskuntavaaleissa 2027 Oulun vaalipiirissä.
              </p>
            </div>
          </div>

          {/* ── AIKAISEMMAT VAALIT ── */}
          <div className="mb-16">
            <div className="relative mb-10 flex items-center gap-3">
              
              <h2 className="font-display text-3xl font-bold italic text-foreground md:text-4xl">
                Vaalit
              </h2>
            </div>

            <div className="space-y-6">
              {[
                {
                  title: "OYY:n edustajistovaalit 2019",
                  body: "Kiinnostukseni politiikkaan alkoi ylioppilaskunnasta, kun syksyn 2019 edustajistovaalien myötä pääsin osaksi edustajistossa työskentelyä. Opintojeni aikana koin tärkeäksi edistää opiskelijoiden asemaa niin yliopistolla kuin Oulussa.",
                  bg: "hsl(38 45% 93%)",
                },
                {
                  title: "Kuntavaalit 2021",
                  body: "Olin ensimmäistä kertaa ehdolla Oulussa kuntavaaleissa 2021. Aloitin vaalien jälkeen Koulutuskuntayhtymä OSAO:n yhtymäkokousedustajana ammatillisen opetuksen kysymysten parissa ja nousin kesken kauden myös sivistyslautakunnan varajäseneksi.",
                  bg: "hsl(206 42% 90%)",
                },
                {
                  title: "Aluevaalit 2022",
                  body: "2022 aluevaalien jälkeen toimin ensin Oulun alueellisen neuvottelukunnan varajäsenenä ja myöhemmin nousin varajäseneksi myös yhdyspintalautakuntaan.",
                  bg: "hsl(340 28% 91%)",
                },
                {
                  title: "Vaalityöntekijä – EK 2023, Presidentti- ja Euro 2024",
                  body: "Vuosina 2022–2025 työskentelin Pohjois-Pohjanmaan Kokoomuksella vaalityöntekijänä. Puolueen yhteisen kampanjan tekeminen eduskuntavaaleissa 2023, presidentinvaaleissa ja eurovaaleissa 2024 sekä kunta- ja aluevaaleissa 2025 opetti paljon politiikan arjesta.",
                  bg: "hsl(42 80% 88%)",
                },
                {
                  title: "Kunta- ja aluevaalit 2025",
                  body: "Keväällä 2025 olin jälleen myös itse ehdolla kunta- ja aluevaaleissa. Vaalien jälkeen jatkoin toimimista OSAO:n yhtymäkokousedustajana sekä aloitin Pohteella turvallisuus- ja valmiuslautakunnan varajäsenenä.",
                  bg: "hsl(195 40% 90%)",
                },
                {
                  title: "Arinan edustajistovaalit 2026",
                  body: "Tulin valituksi Arinan edustajistoon 621 äänellä vuosiksi 2026–2030.",
                  bg: "hsl(20 45% 91%)",
                },
              ].map((c) => (
                <article
                  key={c.title}
                  className="rounded-[1.6rem] border border-border/60 p-8 shadow-sm md:p-10"
                  style={{ background: c.bg }}
                >
                  <h3 className="mb-4 font-display text-xl font-bold italic leading-snug text-secondary md:text-2xl">
                    {c.title}
                  </h3>
                  <p className="text-lg leading-relaxed text-foreground/85">{c.body}</p>
                </article>
              ))}
            </div>
          </div>

          {/* ── VISIO ── */}
          <div className="mb-16">
            <div className="relative mb-10 flex items-center gap-3">
              
              <h2 className="font-display text-3xl font-bold italic text-foreground md:text-4xl">
                Visio
              </h2>
            </div>

            <div className="rounded-2xl border border-border bg-card/85 p-8 shadow-sm md:p-12">
              <p className="text-lg leading-relaxed text-foreground/80">Tulossa pian.</p>
            </div>
          </div>

          {/* ── MIKSI KOKOOMUS ── */}
          <div>
            <div className="relative mb-10 flex items-center gap-3">
              
              <h2 className="font-display text-3xl font-bold italic text-foreground md:text-4xl">
                Miksi kokoomus?
              </h2>
            </div>

            <div className="rounded-2xl border border-border bg-card/85 p-8 shadow-sm md:p-12">
              <div className="space-y-4 text-lg leading-relaxed text-foreground/80">
                <p>
                  Ajattelen, että oman työn kuuluu aina kantaa hedelmää ja että työnteko on yksi tärkeä tapa osallistua yhteiskuntaan. Ajattelen, että oikeistolaista talouspolitiikkaa voi toteuttaa myös inhimillisesti sinisellä sydämellä. Kokoomuksen arvot vapaus, vastuu, demokratia, sivistys ja välittäminen ovat myös minulle tärkeitä.
                </p>
                <p>
                  Sivistyneessä yhteiskunnassa arvostetaan erilaisuutta ja osataan asettua toisten ihmisten asemaan. Sellaista yhteiskuntaa ei aja polarisaatio eikä populismi. Suomen tulee olla paikka, jossa jokaisella on yhtäläinen mahdollisuus menestyä ja elää hyvää elämää erilaisuuksistaan riippumatta.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── LAHJOITA ── */}
    <section
      className="relative overflow-hidden px-6 py-20 lg:px-10"
      style={{
        background: "linear-gradient(180deg, hsl(40 86% 73%) 0%, hsl(39 82% 92%) 100%)",
      }}
    >
      <div className="mx-auto max-w-2xl">
        <div className="rounded-[1.8rem] border border-border bg-card/90 p-10 text-center shadow-lg md:p-14">
          <Heart className="mx-auto mb-5 h-10 w-10 text-primary" />
          <h2 className="mb-3 font-display text-3xl font-bold text-foreground md:text-4xl">
            Lahjoita vaalikampanjaan
          </h2>
          <div className="mx-auto mb-5 h-[3px] w-32 rounded-full bg-primary" />
          <div className="space-y-3 text-lg leading-relaxed text-foreground/80">
            <p>Vaalituen koordinoinnista vastaa tukiyhdistykseni<br />Sinisellä Sydämellä ry.</p>
            <p>Lahjoituksia seuraavaan vaalikampanjaani voi tehdä jälleen lokakuussa 2026.</p>
            <p>Lisätiedot päivittyvät tänne lähempänä.</p>
          </div>
        </div>
      </div>
    </section>
  </Layout>
);

export default Elections;
